import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import hrService from '../../../services/hrService';
import { formatEasternDateOnly } from '../../../utils/timezone';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, employeeId: null, employeeName: '' });

  // UPDATED: State for filtering - simplified to match requirements
  const [statusFilter, setStatusFilter] = useState('active');
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);

  // NEW: State for password update modal
  const [passwordModal, setPasswordModal] = useState({ 
    show: false, 
    userId: null, 
    employeeName: '', 
    newPassword: '', 
    confirmPassword: '' 
  });

  const navigate = useNavigate();

  // Helper function to safely get employee name from various data structures
  const getEmployeeName = (employee) => {
    const profile = employee?.userProfiles || employee?.user_profiles;
    return profile?.fullName ||
      profile?.full_name ||
      employee?.fullName ||
      employee?.full_name ||
      'Unknown Employee';
  };

  // Helper function to safely get employee username
  const getEmployeeUsername = (employee) => {
    const profile = employee?.userProfiles || employee?.user_profiles;
    return profile?.username || employee?.username || null;
  };

  // Helper function to safely get employee avatar URL
  const getEmployeeAvatarUrl = (employee) => {
    const profile = employee?.userProfiles || employee?.user_profiles;
    return profile?.avatarUrl || profile?.avatar_url || null;
  };

  // PERFORMANCE FIX: Track ongoing API calls to prevent duplicates
  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const lastFilterRef = useRef({ statusFilter: 'active', showInactiveOnly: false });

  // PERFORMANCE FIX: Memoized loadData function with deduplication
  const loadData = useCallback(async (force = false) => {
    // Prevent duplicate API calls
    if (loadingRef?.current && !force) {
      console.log('⏳ Skipping duplicate loadData call - already loading');
      return;
    }

    // Check if filters have actually changed
    const filtersChanged =
      lastFilterRef?.current?.statusFilter !== statusFilter ||
      lastFilterRef?.current?.showInactiveOnly !== showInactiveOnly;

    // If filters changed, clear the cache to force fresh data
    if (filtersChanged) {
      console.log('🗑️ Filters changed, clearing employee cache');
      hrService?.clearEmployeeCache?.();
    }

    // Skip if nothing changed and we've already loaded
    if (!filtersChanged && initialLoadDone?.current && !force) {
      console.log('⏳ Skipping loadData - filters unchanged');
      return;
    }

    loadingRef.current = true;
    lastFilterRef.current = { statusFilter, showInactiveOnly };

    setLoading(true);
    setError('');
    try {
      console.log('Loading employees with filters:', { statusFilter, showInactiveOnly });

      // ENHANCED: Load employees with filtering options and better error handling
      const employeeData = await hrService?.getAllEmployeesByStatus?.({
        status: statusFilter,
        showInactiveOnly: showInactiveOnly
      });

      console.log('Loaded employees:', employeeData?.length || 0);
      setEmployees(employeeData || []);
      initialLoadDone.current = true;
    } catch (err) {
      console.error('Error loading employee data:', err);

      // ENHANCED: Better error handling for common issues
      let errorMessage = err?.message || 'Failed to load data';

      if (err?.message?.includes('Cannot coerce the result to a single JSON object')) {
        errorMessage = 'Data loading issue detected. Some user records may need setup. Contact your administrator.';
      } else if (err?.message?.includes('Insufficient permissions')) {
        errorMessage = 'You do not have permission to view employee data.';
      } else if (err?.message?.includes('Employee record not found')) {
        errorMessage = 'Employee data setup required. Please contact HR.';
      }

      setError(errorMessage);
      setEmployees([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [statusFilter, showInactiveOnly]);

  // PERFORMANCE FIX: Single useEffect for initial load and filter changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle success messages from URL params (separate effect, runs once)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams?.get('success');

    if (success === 'employee_created') {
      // Show success message and reload employee data
      console.log('Employee created successfully, refreshing list...');
      loadData(true); // Force reload

      // Clean up URL parameters
      const newUrl = window.location?.pathname + window.location?.search?.replace(/[?&]success=employee_created/, '');
      window.history?.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddEmployee = () => {
    navigate('/employee-profile');
  };

  // Show employee details - navigates to employee profile view mode
  const handleShowEmployee = (employeeId) => {
    navigate(`/employee-profile?id=${employeeId}&mode=view`);
  };

  // Edit employee - navigates to employee profile edit mode
  const handleEditEmployee = (employeeId) => {
    navigate(`/employee-profile?id=${employeeId}&mode=edit`);
  };

  // Delete single employee
  const handleDeleteEmployee = (employee) => {
    setDeleteConfirm({
      show: true,
      employeeId: employee?.id,
      employeeName: getEmployeeName(employee)
    });
  };

  // Confirm delete single employee
  const confirmDeleteEmployee = async () => {
    const employeeIdToDelete = deleteConfirm?.employeeId;
    
    try {
      setLoading(true);
      
      // OPTIMISTIC UPDATE: Immediately remove employee from list
      setEmployees(prevEmployees => 
        prevEmployees?.filter(emp => emp?.id !== employeeIdToDelete)
      );
      
      // Close confirmation dialog immediately
      setDeleteConfirm({ show: false, employeeId: null, employeeName: '' });
      
      // Perform hard delete (permanent deletion)
      await hrService?.deleteEmployee(employeeIdToDelete);

      // Show success message
      console.log('Employee deleted successfully');

      // Force reload employee list to ensure data is in sync
      await loadData(true); // Force reload

    } catch (err) {
      console.error('Error deleting employee:', err);
      setError(err?.message || 'Failed to delete employee');
      
      // On error, reload data to restore correct state
      await loadData(true);
    } finally {
      setLoading(false);
    }
  };

  // Remove multiple employees (bulk delete)
  const handleRemoveEmployees = async () => {
    if (selectedEmployees?.length === 0) {
      alert('Please select employees to remove');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to remove ${selectedEmployees?.length} selected employee(s)? This action cannot be undone.`);
    if (!confirmed) return;

    const employeesToDelete = [...selectedEmployees]; // Store IDs before clearing

    try {
      setLoading(true);

      // OPTIMISTIC UPDATE: Immediately remove selected employees from list
      setEmployees(prevEmployees => 
        prevEmployees?.filter(emp => !employeesToDelete?.includes(emp?.id))
      );
      
      // Clear selection immediately
      setSelectedEmployees([]);

      // Delete selected employees one by one (permanent deletion)
      for (const employeeId of employeesToDelete) {
        await hrService?.deleteEmployee(employeeId);
      }

      console.log(`${employeesToDelete?.length} employees removed successfully`);

      // Force reload data to ensure everything is in sync
      await loadData(true); // Force reload

    } catch (err) {
      console.error('Error removing employees:', err);
      setError(err?.message || 'Failed to remove employees');
      
      // On error, reload data to restore correct state
      await loadData(true);
    } finally {
      setLoading(false);
    }
  };

  // ENHANCED: Handle employment status changes with better error handling
  const handleStatusChange = async (employeeId, newStatus) => {
    try {
      // Show loading state for this specific employee
      const updatedEmployees = employees?.map(emp =>
        emp?.id === employeeId ? { ...emp, loading: true } : emp
      );
      setEmployees(updatedEmployees);

      // Update employee status using existing updateEmployee method
      await hrService?.updateEmployee(employeeId, {
        employmentStatus: newStatus
      });

      console.log('Employee status updated successfully to:', newStatus);

      // CRITICAL FIX: Clear cache and force reload to refresh the filtered list
      hrService?.clearEmployeeCache?.();
      await loadData(true);

    } catch (err) {
      console.error('Error updating employee status:', err);

      // ENHANCED: Better error messages
      let errorMessage = err?.message || 'Failed to update employee status';

      if (err?.message?.includes('Cannot coerce')) {
        errorMessage = 'Employee record not found. The employee may have been deleted or needs setup.';
      } else if (err?.message?.includes('Insufficient permissions')) {
        errorMessage = 'You do not have permission to update employee status.';
      }

      setError(errorMessage);

      // Remove loading state on error
      const updatedEmployees = employees?.map(emp =>
        emp?.id === employeeId ? { ...emp, loading: false } : emp
      );
      setEmployees(updatedEmployees);
    }
  };

  // NEW: Handle password update modal open
  const handleEditPassword = (employee) => {
    const profile = employee?.userProfiles || employee?.user_profiles;
    const userId = profile?.id || employee?.userId || employee?.user_id;
    
    if (!userId) {
      setError('Cannot update password: User ID not found');
      return;
    }

    setPasswordModal({
      show: true,
      userId: userId,
      employeeName: getEmployeeName(employee),
      newPassword: '',
      confirmPassword: ''
    });
  };

  // NEW: Handle password update submission
  const handlePasswordUpdate = async () => {
    try {
      // Validation
      if (!passwordModal?.newPassword || passwordModal?.newPassword?.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      if (passwordModal?.newPassword !== passwordModal?.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      setLoading(true);
      setError('');

      // Update password
      await hrService?.updateEmployeePassword(
        passwordModal?.userId,
        passwordModal?.newPassword
      );

      // Close modal and reset state
      setPasswordModal({ 
        show: false, 
        userId: null, 
        employeeName: '', 
        newPassword: '', 
        confirmPassword: '' 
      });

      // Show success message (you can add a success state if needed)
      console.log('Password updated successfully');
      
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  // Handle employee selection for bulk operations
  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev?.includes(employeeId)) {
        return prev?.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  // Handle select all employees
  const handleSelectAllEmployees = () => {
    if (selectedEmployees?.length === employees?.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees?.map(emp => emp?.id));
    }
  };

  // UPDATED: Toggle between active and inactive employees
  const toggleInactiveView = () => {
    const newShowInactiveOnly = !showInactiveOnly;
    setShowInactiveOnly(newShowInactiveOnly);
    // When toggling to inactive view, clear status filter
    // When toggling back to active view, set to 'active' by default
    setStatusFilter(newShowInactiveOnly ? '' : 'active');
    setSelectedEmployees([]); // Clear selection
  };

  // UPDATED: Get status color for the limited status options
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'terminated': return 'destructive';
      case 'resigned': return 'warning';
      default: return 'muted';
    }
  };

  const renderEmptyState = (type) => {
    const content = showInactiveOnly
      ? {
        icon: 'UserX',
        title: 'No inactive employees found',
        description: 'All employees are currently active',
        actionLabel: 'View Active Employees',
        action: () => setShowInactiveOnly(false)
      }
      : {
        icon: 'Users',
        title: 'No employees found',
        description: 'Add your first employee to get started',
        actionLabel: 'Add New Employee',
        action: handleAddEmployee
      };

    return (
      <div className="text-center py-8">
        <Icon name={content?.icon} size={48} color="var(--color-muted)" className="mx-auto mb-4" />
        <p className="text-muted-foreground mb-2">{content?.title}</p>
        <p className="text-sm text-muted-foreground mb-4">{content?.description}</p>
        {content?.action && (
          <Button onClick={content?.action} variant="outline" size="sm">
            <Icon name="Plus" size={16} className="mr-2" />
            {content?.actionLabel}
          </Button>
        )}
      </div>
    );
  };

  const renderLoadingState = () => (
    <div className="space-y-3">
      {[1, 2, 3]?.map((i) => (
        <div
          key={i}
          className="flex items-center justify-between p-3 rounded-lg border border-border animate-pulse"
        >
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 bg-muted rounded"></div>
            <div className="w-10 h-10 rounded-full bg-muted"></div>
            <div>
              <div className="w-32 h-4 bg-muted rounded mb-2"></div>
              <div className="w-48 h-3 bg-muted rounded"></div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-16 h-6 bg-muted rounded"></div>
            <div className="w-20 h-8 bg-muted rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-8">
      <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
      <p className="text-destructive mb-2">Error loading data</p>
      <p className="text-sm text-muted-foreground mb-4">{error}</p>
      {/* ENHANCED: Add helpful action buttons based on error type */}
      <div className="space-y-2">
        <Button onClick={loadData} variant="outline" size="sm">
          <Icon name="RefreshCw" size={16} className="mr-2" />
          Try Again
        </Button>
        {error?.includes('setup') || error?.includes('Data loading issue') ? (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 mb-2">
              <strong>Need Help?</strong> If you continue seeing this error:
            </p>
            <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
              <li>Contact your system administrator</li>
              <li>Ensure your user account has proper employee setup</li>
              <li>Check if you have the required permissions</li>
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (error && !employees?.length) {
    return (
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Employee Management</h2>
          <Button onClick={handleAddEmployee} variant="default" size="sm">
            <Icon name="Plus" size={16} className="mr-2" />
            Add Employee
          </Button>
        </div>
        {renderErrorState()}
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6">
      {/* UPDATED: Header without pending actions/tabs */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Employee Management</h2>
        <div className="flex items-center space-x-2">
          <Button onClick={handleAddEmployee} variant="default" size="sm">
            <Icon name="Plus" size={16} className="mr-2" />
            Add Employee
          </Button>
        </div>
      </div>
      {/* UPDATED: Filter controls - simplified according to requirements */}
      <div className="mb-4 flex items-center justify-between bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center space-x-3">
          {/* UPDATED: Status Filter Dropdown - only shows required options */}
          {!showInactiveOnly && (
            <div className="flex items-center space-x-2">
              <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="active">Active</option>
                <option value="terminated">Terminated</option>
                <option value="resigned">Resigned</option>
              </select>
            </div>
          )}

          {/* Employee Count */}
          <span className="text-sm text-gray-600">
            {showInactiveOnly
              ? `${employees?.length || 0} inactive employees`
              : `${employees?.length || 0} employees ${statusFilter ? `(${statusFilter})` : ''}`
            }
          </span>
        </div>

        <div className="flex items-center space-x-3">
          {/* UPDATED: Inactive Employees Toggle - as requested */}
          <Button
            variant={showInactiveOnly ? "default" : "outline"}
            size="sm"
            onClick={toggleInactiveView}
          >
            <Icon name={showInactiveOnly ? "Users" : "UserX"} size={16} className="mr-2" />
            {showInactiveOnly ? "Active Employees" : "Inactive Employees"}
          </Button>
        </div>
      </div>
      {/* Bulk Actions for Employee List */}
      {employees?.length > 0 && (
        <div className="mb-4 flex items-center justify-between bg-gray-50 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={selectedEmployees?.length === employees?.length}
              onChange={handleSelectAllEmployees}
              className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              {selectedEmployees?.length > 0
                ? `${selectedEmployees?.length} of ${employees?.length} selected`
                : `Select all ${employees?.length} employees`
              }
            </span>
          </div>

          {selectedEmployees?.length > 0 && (
            <Button
              onClick={handleRemoveEmployees}
              variant="destructive"
              size="sm"
            >
              <Icon name="Trash2" size={16} className="mr-2" />
              Remove Selected ({selectedEmployees?.length})
            </Button>
          )}
        </div>
      )}
      {loading ? (
        renderLoadingState()
      ) : (
        <>
          {employees?.length === 0 ? (
            renderEmptyState('employees')
          ) : (
            <div className="space-y-3">
              {employees?.map((employee) => (
                <div
                  key={employee?.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-background/50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedEmployees?.includes(employee?.id)}
                      onChange={() => handleSelectEmployee(employee?.id)}
                      className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                    />

                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {getEmployeeAvatarUrl(employee) ? (
                        <img
                          src={getEmployeeAvatarUrl(employee)}
                          alt={getEmployeeName(employee)}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <Icon name="User" size={16} color="var(--color-primary)" />
                      )}
                    </div>

                    <div>
                      <p className="font-medium text-foreground">
                        {getEmployeeName(employee)}
                        {getEmployeeUsername(employee) && (
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            (@{getEmployeeUsername(employee)})
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {employee?.position} • {employee?.department} • ID: {employee?.employeeId || employee?.employee_id}
                      </p>
                      <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                        {(employee?.dateOfJoining || employee?.date_of_joining) && (
                          <div className="flex items-center">
                            <Icon name="Calendar" size={12} className="mr-1" />
                            <span>Joined: {formatEasternDateOnly(employee?.dateOfJoining || employee?.date_of_joining)}</span>
                          </div>
                        )}
                        {(employee?.branchName || employee?.branch_name) && (
                          <div className="flex items-center">
                            <Icon name="MapPin" size={12} className="mr-1" />
                            <span>Location: {employee?.branchName || employee?.branch_name}</span>
                          </div>
                        )}
                        {(employee?.reportingManagerName || employee?.reporting_manager_name) && (
                          <div className="flex items-center">
                            <Icon name="User" size={12} className="mr-1" />
                            <span>Reports to: {employee?.reportingManagerName || employee?.reporting_manager_name}</span>
                          </div>
                        )}
                        {(() => {
                          const profile = employee?.userProfiles || employee?.user_profiles;
                          const city = profile?.city;
                          const state = profile?.state;
                          const zip = profile?.zip;
                          if (city || state || zip) {
                            return (
                              <div className="flex items-center">
                                <Icon name="Home" size={12} className="mr-1" />
                                <span>
                                  {[city, state, zip]?.filter(Boolean)?.join(', ')}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* UPDATED: Status dropdown for individual employees - restricted options */}
                    <div className="relative">
                      {employee?.loading ? (
                        <div className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full flex items-center">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-700 mr-1"></div>
                          Updating...
                        </div>
                      ) : (
                        <select
                          value={employee?.employmentStatus || employee?.employment_status || 'active'}
                          onChange={(e) => handleStatusChange(employee?.id, e?.target?.value)}
                          disabled={employee?.loading}
                          className={`
                            text-xs px-2 py-1 rounded-full border-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary
                            ${getStatusColor(employee?.employmentStatus || employee?.employment_status) === 'success' ? 'bg-green-100 text-green-700' : ''}
                            ${getStatusColor(employee?.employmentStatus || employee?.employment_status) === 'warning' ? 'bg-yellow-100 text-yellow-700' : ''}
                            ${getStatusColor(employee?.employmentStatus || employee?.employment_status) === 'muted' ? 'bg-gray-100 text-gray-700' : ''}
                            ${getStatusColor(employee?.employmentStatus || employee?.employment_status) === 'destructive' ? 'bg-red-100 text-red-700' : ''}
                          `}
                        >
                          <option value="active">Active</option>
                          <option value="terminated">Terminated</option>
                          <option value="resigned">Resigned</option>
                        </select>
                      )}
                    </div>

                    {/* Action Buttons: Show, Edit, Delete */}
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShowEmployee(employee?.id)}
                        title="Show Employee Details"
                      >
                        <Icon name="Eye" size={16} />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditEmployee(employee?.id)}
                        title="Edit Employee"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditPassword(employee)}
                        title="Update Password"
                      >
                        <Icon name="Key" size={16} />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEmployee(employee)}
                        title="Delete Employee"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm?.employeeName}</strong>?
              This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ show: false, employeeId: null, employeeName: '' })}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteEmployee}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Employee'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Password Update Modal */}
      {passwordModal?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <Icon name="Key" size={20} className="text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Update Password</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Update password for <strong>{passwordModal?.employeeName}</strong>
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordModal?.newPassword}
                  onChange={(e) => setPasswordModal(prev => ({ ...prev, newPassword: e?.target?.value }))}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={passwordModal?.confirmPassword}
                  onChange={(e) => setPasswordModal(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setPasswordModal({ 
                    show: false, 
                    userId: null, 
                    employeeName: '', 
                    newPassword: '', 
                    confirmPassword: '' 
                  });
                  setError('');
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handlePasswordUpdate}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
